import { Suspense, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, Hammer } from "@phosphor-icons/react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { bespokeIcon } from "../lib/bespoke";
import { bespokeComponent } from "../bespoke/registry";

export default function BespokeModulePage() {
  const { key } = useParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setMeta(null); setError(null);
    API.get(`/bespoke/${key}/access`).then(({ data }) => setMeta(data)).catch((e) => setError(e.response?.status === 404 ? "missing" : "locked"));
  }, [key]);

  if (error === "missing") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="bespoke-module-missing">
        <h1 className="text-xl font-bold font-[Chivo]">No such module</h1>
        <p className="text-sm text-muted-foreground mt-2">There's no bespoke module called <span className="font-mono">{key}</span>.</p>
        <Button asChild variant="outline" size="sm" className="mt-4"><Link to="/">Back to dashboard</Link></Button>
      </div>
    );
  }
  if (error === "locked") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="bespoke-module-locked">
        <Lock size={40} className="mx-auto text-muted-foreground" />
        <h1 className="text-xl font-bold font-[Chivo] mt-3">This module isn't switched on</h1>
        <p className="text-sm text-muted-foreground mt-2">Bespoke modules are added to your workspace by the Fixlr team. Ask for it from Billing.</p>
        {["super_admin", "admin"].includes(user?.role) && <Button asChild size="sm" className="mt-4"><Link to="/platform/billing" data-testid="bespoke-module-locked-billing">Open Billing</Link></Button>}
      </div>
    );
  }
  if (!meta) return <div className="text-center py-20 text-muted-foreground" data-testid="bespoke-module-loading">Loading…</div>;

  const Icon = bespokeIcon(meta.icon);
  const Component = bespokeComponent(meta.key);
  if (!Component || meta.status !== "live") {
    const building = meta.status !== "live";
    return (
      <div className="max-w-xl mx-auto py-16" data-testid="bespoke-module-placeholder">
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto"><Icon size={30} weight="duotone" className="text-primary" /></div>
          <h1 className="text-2xl font-bold font-[Chivo] mt-4" data-testid="bespoke-module-name">{meta.name}</h1>
          {meta.description && <p className="text-sm text-muted-foreground mt-2">{meta.description}</p>}
          <p className="text-xs text-muted-foreground mt-5 inline-flex items-center gap-1.5" data-testid="bespoke-module-building-note"><Hammer size={14} /> {building ? "This module is switched on for your workspace and the Fixlr team is building it — it appears in your menu the moment it goes live." : "The Fixlr team is building this module — its screens will appear here."}</p>
        </div>
      </div>
    );
  }
  return (
    <Suspense fallback={<div className="text-center py-20 text-muted-foreground">Loading {meta.name}…</div>}>
      <Component module={meta} user={user} />
    </Suspense>
  );
}
